import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Copy, RefreshCw, Lock, ShieldCheck, Webhook, Handshake, Key, CreditCard, ExternalLink } from "lucide-react";

const copy = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  } catch { toast.error("Falha ao copiar"); }
};

function Check({ ok, label, hint }) {
  return (
    <div className="flex items-start gap-2 py-1.5" data-testid={`check-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {ok ? <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0"/> : <XCircle size={14} className="text-red-500 mt-0.5 shrink-0"/>}
      <div className="flex-1 min-w-0">
        <div className="text-sm">{label}</div>
        {!ok && hint && <div className="text-[11px] text-neutral-500 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function CopyRow({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="mt-3 rounded-lg bg-neutral-50 border border-neutral-200 p-2.5 flex items-center gap-2" data-testid={testid}>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">{label}</div>
        <div className="font-mono text-[11px] text-neutral-800 truncate mt-0.5">{value}</div>
      </div>
      <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => copy(value, label)} data-testid={`${testid}-copy`}>
        <Copy size={12}/>
      </Button>
    </div>
  );
}

export default function AdminIntegrations() {
  const { user } = useAuth();
  const isCEO = user?.role === "superadmin" && Array.isArray(user?.tags) && user.tags.includes("ceo");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isCEO) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integrations/mp/health");
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao carregar"); }
    finally { setLoading(false); }
  }, [isCEO]);

  useEffect(() => { load(); }, [load]);

  if (!isCEO) {
    return (
      <div className="p-6 lg:p-8 max-w-3xl">
        <div className="rounded-2xl border border-neutral-200 bg-white p-8 flex items-start gap-4" data-testid="integrations-locked">
          <div className="w-10 h-10 rounded-lg bg-neutral-100 grid place-items-center"><Lock size={18}/></div>
          <div>
            <h1 className="font-heading font-black text-2xl">Restrito ao CEO</h1>
            <p className="text-sm text-neutral-600 mt-1">A tela de integrações expõe URLs e status de configuração da plataforma. Só o CEO pode ver.</p>
          </div>
        </div>
      </div>
    );
  }

  const m = data?.marketplace || {};
  const p = data?.platform_checkout || {};
  const w = data?.webhook || {};

  const mpOverallOk = m.configured && p.access_token_set;
  const webhookOk = w.secret_configured;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">Plataforma</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Integrações</h1>
          <p className="mt-2 text-sm text-neutral-600">Health check das integrações de pagamento. Copie as URLs abaixo direto pra colar no painel do provedor.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="integrations-refresh"><RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`}/> Atualizar</Button>
      </div>

      {/* Overall status banner */}
      <div className={`rounded-2xl border p-5 mb-6 flex items-start gap-3 ${mpOverallOk && webhookOk ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="integrations-overall">
        <ShieldCheck size={22} className={mpOverallOk && webhookOk ? "text-emerald-700" : "text-amber-700"}/>
        <div>
          <div className="font-heading font-bold">{mpOverallOk && webhookOk ? "Mercado Pago pronto pra operar em produção" : "Ainda falta configurar algo no painel MP"}</div>
          <div className="text-sm text-neutral-700 mt-0.5">{mpOverallOk && webhookOk ? "Marketplace + Checkout + Webhook validados no backend. Rode um pagamento de teste pra confirmar ponta a ponta." : "Confira os cards abaixo — o que estiver com ❌ vermelho ainda precisa de ação."}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Marketplace OAuth */}
        <div className="bg-white border border-neutral-200 rounded-2xl p-6" data-testid="card-marketplace">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 bg-neutral-900 text-white rounded-lg grid place-items-center"><Handshake size={16}/></div>
            <div>
              <div className="font-heading font-bold">Marketplace OAuth</div>
              <div className="text-[11px] text-neutral-500">Split de pagamentos por franqueado</div>
            </div>
          </div>
          <div className="divide-y divide-neutral-100">
            <Check ok={m.client_id_set} label="MP_CLIENT_ID configurado" hint="Copie da app em MP Developers → Credenciais e ponha no .env do backend."/>
            <Check ok={m.client_secret_set} label="MP_CLIENT_SECRET configurado" hint="Nunca versionar. Guarde só no .env do servidor."/>
            <Check ok={m.token_encryption_configured} label="Chave Fernet para cifrar tokens em repouso" hint="Gere com Fernet.generate_key() e ponha em MP_TOKEN_ENCRYPTION_KEY."/>
            <Check ok={!!m.redirect_uri} label="Redirect URI definida no servidor" hint="Setar em MP_REDIRECT_URI."/>
          </div>
          <CopyRow label="Redirect URI (registre no painel MP)" value={m.redirect_uri} testid="marketplace-redirect-uri"/>
          <a href="https://www.mercadopago.com.br/developers/panel/app" target="_blank" rel="noreferrer" className="mt-3 text-[11px] text-neutral-600 hover:text-neutral-900 flex items-center gap-1" data-testid="marketplace-panel-link"><ExternalLink size={10}/> Abrir painel MP Developers</a>
        </div>

        {/* Platform Checkout */}
        <div className="bg-white border border-neutral-200 rounded-2xl p-6" data-testid="card-checkout">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 bg-neutral-900 text-white rounded-lg grid place-items-center"><CreditCard size={16}/></div>
            <div>
              <div className="font-heading font-bold">Checkout Pro (plataforma)</div>
              <div className="text-[11px] text-neutral-500">Token da matriz — usado quando a loja não está conectada via OAuth</div>
            </div>
          </div>
          <div className="divide-y divide-neutral-100">
            <Check ok={p.access_token_set} label="MP_ACCESS_TOKEN configurado" hint="Copie de Suas integrações → Credenciais de produção."/>
            <Check ok={p.mode === "production"} label={`Modo: ${p.mode || "—"}`} hint="Em sandbox, os pagamentos não são reais."/>
          </div>
        </div>

        {/* Webhook */}
        <div className="bg-white border border-neutral-200 rounded-2xl p-6 md:col-span-2" data-testid="card-webhook">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 bg-neutral-900 text-white rounded-lg grid place-items-center"><Webhook size={16}/></div>
            <div>
              <div className="font-heading font-bold">Webhook</div>
              <div className="text-[11px] text-neutral-500">MP notifica pagamentos aprovados — validação HMAC-SHA256 + replay window de 5min</div>
            </div>
          </div>
          <div className="divide-y divide-neutral-100">
            <Check ok={w.secret_configured} label="MP_WEBHOOK_SECRET configurado" hint="Sem o secret a assinatura não é validada — QUALQUER um pode notificar pagamento."/>
            <Check ok={w.signature_validation === "enforced"} label={`Validação de assinatura: ${w.signature_validation || "—"}`}/>
            <Check ok={!!w.replay_window_seconds} label={`Replay protection: ${w.replay_window_seconds ?? "?"}s`} hint="Rejeita requisições com timestamp fora dessa janela."/>
          </div>
          <CopyRow label="URL do Webhook (registre no painel MP → Webhooks)" value={w.url_production} testid="webhook-prod-url"/>
          {w.url_preview && <CopyRow label="URL do Webhook — Preview (opcional)" value={w.url_preview} testid="webhook-preview-url"/>}
          <div className="mt-3 flex items-start gap-2 text-[11px] bg-neutral-50 border border-neutral-200 rounded-lg p-3" data-testid="webhook-instructions">
            <Key size={12} className="mt-0.5 text-neutral-500 shrink-0"/>
            <div className="text-neutral-700 leading-relaxed">
              <b>Como registrar:</b> Painel MP Developers → sua app → aba <b>Webhooks</b> → cole a URL acima, marque só o evento <b>Payment</b> e cole a chave secreta que está em <span className="font-mono">MP_WEBHOOK_SECRET</span> no <span className="font-mono">.env</span>. Depois faça um pagamento de teste — se o log do backend mostrar <span className="font-mono">200 received</span>, tá tudo certo.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
